// Performance optimization utilities for map rendering

export interface MarkerCluster {
  id: string;
  count: number;
  coordinates: [number, number];
  bounds: [[number, number], [number, number]];
  markers: any[];
}

// Debounce function for search and map interactions
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// Throttle function for scroll and pan events
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// Simple marker clustering algorithm
export function clusterMarkers(
  markers: any[],
  zoom: number,
  gridSize: number = 60
): MarkerCluster[] {
  if (zoom >= 15) return []; // No clustering at high zoom levels
  
  const clusters: MarkerCluster[] = [];
  const processed = new Set<number>();
  
  markers.forEach((marker, index) => {
    if (processed.has(index)) return;
    
    const cluster: MarkerCluster = {
      id: `cluster-${index}`,
      count: 1,
      coordinates: [marker.lat, marker.lng],
      bounds: [[marker.lat, marker.lng], [marker.lat, marker.lng]],
      markers: [marker]
    };
    
    // Find nearby markers to cluster
    markers.forEach((otherMarker, otherIndex) => {
      if (otherIndex === index || processed.has(otherIndex)) return;
      
      const distance = getDistance(
        marker.lat, marker.lng,
        otherMarker.lat, otherMarker.lng
      );
      
      // Cluster if within grid size (adjusted for zoom)
      const clusterRadius = gridSize / Math.pow(2, zoom - 10);
      if (distance < clusterRadius) {
        cluster.markers.push(otherMarker);
        cluster.count++;
        processed.add(otherIndex);
        
        // Update bounds
        cluster.bounds[0][0] = Math.min(cluster.bounds[0][0], otherMarker.lat);
        cluster.bounds[0][1] = Math.min(cluster.bounds[0][1], otherMarker.lng);
        cluster.bounds[1][0] = Math.max(cluster.bounds[1][0], otherMarker.lat);
        cluster.bounds[1][1] = Math.max(cluster.bounds[1][1], otherMarker.lng);
      }
    });
    
    // Update cluster center
    if (cluster.count > 1) {
      const centerLat = cluster.markers.reduce((sum, m) => sum + m.lat, 0) / cluster.count;
      const centerLng = cluster.markers.reduce((sum, m) => sum + m.lng, 0) / cluster.count;
      cluster.coordinates = [centerLat, centerLng];
    }
    
    clusters.push(cluster);
    processed.add(index);
  });
  
  return clusters;
}

// Calculate distance between two points (Haversine formula)
function getDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

// Cache management for search results and map data
export class MapCache {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  
  set(key: string, data: any, ttl: number = 300000): void { // 5 minutes default
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }
  
  get(key: string): any | null {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return item.data;
  }
  
  clear(): void {
    this.cache.clear();
  }
  
  cleanup(): void {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        this.cache.delete(key);
      }
    }
  }
}

// Viewport-based marker filtering
export function filterMarkersInViewport(
  markers: any[],
  bounds: [[number, number], [number, number]]
): any[] {
  const [sw, ne] = bounds;
  return markers.filter(marker => 
    marker.lat >= sw[0] && marker.lat <= ne[0] &&
    marker.lng >= sw[1] && marker.lng <= ne[1]
  );
}

// Lazy loading for map tiles
export function preloadTiles(map: any, bounds: any, zoomLevels: number[] = [1, 2]): void {
  zoomLevels.forEach(zoomOffset => {
    const currentZoom = map.getZoom();
    const targetZoom = currentZoom + zoomOffset;
    
    if (targetZoom <= map.getMaxZoom()) {
      // Preload tiles at higher zoom levels
      setTimeout(() => {
        const tileLayer = map._layers[Object.keys(map._layers)[0]];
        if (tileLayer && tileLayer._preloadTiles) {
          tileLayer._preloadTiles(bounds, targetZoom);
        }
      }, 100);
    }
  });
}

// Bundle optimization - lazy load map libraries
export async function loadMapLibrary(): Promise<any> {
  if (window.L) return window.L;
  
  // Load CSS
  if (!document.querySelector('link[href*="leaflet"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
  }
  
  // Load JS
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

// Memory management for large datasets
export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

// Efficient re-rendering patterns
export function shouldUpdateMarkers(
  prevMarkers: any[],
  nextMarkers: any[],
  prevBounds: any,
  nextBounds: any
): boolean {
  // Check if bounds changed significantly
  if (prevBounds && nextBounds) {
    const boundsChanged = Math.abs(prevBounds.getNorth() - nextBounds.getNorth()) > 0.001 ||
                         Math.abs(prevBounds.getSouth() - nextBounds.getSouth()) > 0.001 ||
                         Math.abs(prevBounds.getEast() - nextBounds.getEast()) > 0.001 ||
                         Math.abs(prevBounds.getWest() - nextBounds.getWest()) > 0.001;
    
    if (boundsChanged) return true;
  }
  
  // Check if markers changed
  if (prevMarkers.length !== nextMarkers.length) return true;
  
  // Quick shallow comparison
  return prevMarkers.some((marker, index) => 
    !nextMarkers[index] || marker.id !== nextMarkers[index].id
  );
}